import type { AdaptedNavGroup, AdaptedNavItem } from '@command-center/ui-system';
import { adminConfig } from './config';
import { demoInjectedNav, demoNavAllows } from './demo-mode';
import { resolveIcon } from './icons';

export type { AdaptedNavGroup, AdaptedNavItem };

/** Render order. Any unknown groups are appended after these in first-seen order. */
const GROUP_ORDER: string[] = [
  'Command Center',
  // Records/Insights only exist in the Lead Command Center demo nav (injected
  // items in demo-mode.ts) — harmless no-ops for every other portal.
  'Records',
  'Insights',
  'Pipeline',
  'Clients',
  'Work',
  'Billing',
  'AI Systems',
  'Knowledge',
  'Settings',
  'Operations',
  'Overview',
];

export function getNavigationGroups(): AdaptedNavGroup[] {
  const groups = new Map<string, AdaptedNavItem[]>();

  const pushItem = (groupLabel: string, adapted: AdaptedNavItem) => {
    const list = groups.get(groupLabel);
    if (list) list.push(adapted);
    else groups.set(groupLabel, [adapted]);
  };

  // Demo-mode injected items (e.g. the Lead Dashboard) surface first in their
  // group. The routes already exist in App.tsx — this only adds the nav link.
  for (const injected of demoInjectedNav()) {
    pushItem(injected.group || 'Operations', {
      id: injected.id,
      to: injected.route,
      label: injected.label,
      icon: resolveIcon(injected.icon),
    });
  }

  for (const item of adminConfig.navigation) {
    // In a demo mode, hide everything not on the mode's allowlist. Outside a
    // demo mode, demoNavAllows() always returns true (no filtering).
    if (!demoNavAllows(item.id)) continue;
    const groupLabel = item.group || 'Operations';
    const adapted: AdaptedNavItem = {
      id: item.id,
      to: item.route,
      label: item.label,
      icon: resolveIcon(item.icon),
      moduleId: item.moduleId,
      placeholder: item.placeholder,
    };
    pushItem(groupLabel, adapted);
  }

  const orderedKeys = [
    ...GROUP_ORDER.filter((key) => groups.has(key)),
    ...Array.from(groups.keys()).filter((key) => !GROUP_ORDER.includes(key)),
  ];

  const collapsible = adminConfig.navigationVariant === 'workflow-groups';
  return orderedKeys.map((label) => ({ label, items: groups.get(label) ?? [], collapsible }));
}
